"use client";
import { useTranslations } from "next-intl"

import { useState, useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { createPortal } from "react-dom";
import { Filter, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getTaxonomyItems } from "@/lib/taxonomy-cache";
import {
  CONDITION_GRADE_MAX,
  CONDITION_GRADE_MIN,
  CONDITION_GRADE_LABELS,
  GradeRangeFilterPanel,
} from "@/components/feed/grade-range-filter-panel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { FeedActiveTab } from "@/components/feed/feed-page-client-state";
import { FEED_DEFAULT_ACTIVE_TAB } from "@/lib/feed-tab-config";
import { listingFormatFilterTriggerActiveClasses } from "@/lib/listing-format-filter-styles";
import type { DropdownSurface } from "@/lib/dropdown-styles";
import { getCardLanguageFlag } from "@/lib/flags";
import type { ListingCategory as CanonicalListingCategory } from "@kraahl/contract";

interface FilterOption {
  id: string;
  label: string;
  slug?: string | null;
  flag?: string;
}

// `null` = no category filter. The category tuple itself is canonical in
// `@kraahl/contract` (Wave 0, NEW_CATEGORIES_PLAN §3.1) — never restate it.
type ListingCategory = CanonicalListingCategory | null;

export interface FeedFiltersState {
  brands: string[];
  themes: string[];
  categories: string[];
  tcgs?: string[];
  listingFormats?: string[];
  conditions: string[];
  gradeRange: [number, number] | null;
  sortBy: 'price-asc' | 'price-desc' | 'date-asc' | 'date-desc' | null;
  listingCategory?: ListingCategory;
  tcgSets?: string[];
  tcgRarities?: string[];
  cardConditions?: string[];
  cardLanguages?: string[];
  cardFormats?: string[];
  gradingCompanies?: string[];
  cardGradeRange?: [number, number] | null;
  mtgPrintVariants?: string[];
}

interface FeedFiltersProps {
  value: FeedFiltersState;
  onChange: (next: FeedFiltersState) => void;
  renderButton?: (props: {
    onClick: () => void;
    showFilters: boolean;
    hasActiveFilters: boolean;
    activeCount: number;
  }) => React.ReactNode;
  inline?: boolean;
  chipRow?: boolean;
  stripDragRef?: MutableRefObject<boolean>;
  open?: boolean;
  onOpenChangeAction?: (open: boolean) => void;
  activeTab?: FeedActiveTab | "just-listed";
  /**
   * Category-page mode: the listing category is fixed by the route, so the two
   * category toggle pills (Building blocks / Trading card games) are hidden and
   * the caller's `value.listingCategory` stays locked. Sub-filters for the
   * locked category still render. Default `false` (feed/search behaviour).
   */
  lockCategory?: boolean;
}

const LISTING_FORMAT_OPTIONS = [
  { value: "fixed_price", label: "buyNow" },
  { value: "fixed_price_with_offers", label: "buyNowOffers" },
  { value: "offers_only", label: "offersOnly" },
  { value: "auction", label: "auction" },
];

const CONDITIONS = [
  { value: "new", label: "new" },
  { value: "pre-owned", label: "preOwned" },
  { value: "custom", label: "custom" },
];

const MTG_VARIANTS = [
  { value: "alpha", label: "Alpha" },
  { value: "beta", label: "Beta" },
  { value: "unlimited", label: "Unlimited" },
  { value: "revised", label: "Revised" },
  { value: "fbb", label: "FBB" },
  { value: "fwb", label: "FWB" },
  { value: "summer", label: "Summer" },
  { value: "ce", label: "CE" },
  { value: "ice_age", label: "Ice Age" },
];

export function FeedFilters({
  value,
  onChange,
  renderButton,
  inline = false,
  chipRow = false,
  stripDragRef,
  open,
  onOpenChangeAction,
  activeTab = FEED_DEFAULT_ACTIVE_TAB,
  lockCategory = false,
}: FeedFiltersProps) {
  const tFilters = useTranslations("feedFilters");
  const [mounted, setMounted] = useState(false);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const filtersDragRef = useRef<{ active: boolean; startX: number; startY: number }>({
    active: false,
    startX: 0,
    startY: 0,
  });
  const filtersDraggingRef = useRef(false);

  const tFormat = useTranslations("listingFormat");
  const [brands, setBrands] = useState<FilterOption[]>([]);
  const [themes, setThemes] = useState<FilterOption[]>([]);
  const [productTypes, setProductTypes] = useState<FilterOption[]>([]);
  const [tcgOptions, setTcgOptions] = useState<FilterOption[]>([]);
  const [tcgSets, setTcgSets] = useState<FilterOption[]>([]);
  const [tcgRarities, setTcgRarities] = useState<FilterOption[]>([]);
  const [cardLanguages, setCardLanguages] = useState<FilterOption[]>([]);
  const [cardConditions, setCardConditions] = useState<FilterOption[]>([]);
  const [cardFormats, setCardFormats] = useState<FilterOption[]>([]);
  const [gradingCompanies, setGradingCompanies] = useState<FilterOption[]>([]);

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const selectedBrands = value.brands ?? [];
  const selectedThemes = value.themes ?? [];
  const selectedCategories = value.categories ?? [];
  const selectedTcgs = value.tcgs ?? [];
  const selectedTcgSets = value.tcgSets ?? [];
  const selectedTcgRarities = value.tcgRarities ?? [];
  const selectedCardLanguages = value.cardLanguages ?? [];
  const selectedCardConditions = value.cardConditions ?? [];
  const selectedCardFormats = value.cardFormats ?? [];
  const selectedGradingCompanies = value.gradingCompanies ?? [];
  const selectedMtgVariants = value.mtgPrintVariants ?? [];
  const selectedFormats = value.listingFormats ?? [];
  const selectedConditions = value.conditions ?? [];
  const listingCategory: ListingCategory = value.listingCategory ?? null;
  const gradeRange = value.gradeRange ?? null;
  const cardGradeRange = value.cardGradeRange ?? null;
  const sortBy = value.sortBy ?? null;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const el = document.querySelector("[data-feed-filters-host]") as HTMLElement | null;
    setHost(el);
  }, []);

  const isControlled = typeof open === "boolean" && typeof onOpenChangeAction === "function";
  const effectiveShowFilters = isControlled ? open : showFilters;
  const setShowFiltersAction = isControlled ? onOpenChangeAction : setShowFilters;

  useEffect(() => {
    let cancelled = false;
    async function fetchBaseTaxonomy() {
      let brandsData, themesData, productTypesData, tcgsData, langData, cardConditionData, cardFormatData, gradingCoData;
      try {
        [
          brandsData,
          themesData,
          productTypesData,
          tcgsData,
          langData,
          cardConditionData,
          cardFormatData,
          gradingCoData,
        ] = await Promise.all([
          getTaxonomyItems({ type: "brand" }),
          getTaxonomyItems({ type: "theme" }),
          getTaxonomyItems({ type: "product_type" }),
          getTaxonomyItems({ type: "tcg", parent: { mode: "root" } }),
          getTaxonomyItems({ type: "card_language" }),
          getTaxonomyItems({ type: "card_condition" }),
          getTaxonomyItems({ type: "card_format" }),
          getTaxonomyItems({ type: "grading_company" }),
        ]);
      } catch {
        return; // leave options empty on load failure (prior behavior)
      }
      if (cancelled) return;

      const uniqueBrands = Array.from(
        new Map(brandsData.map((b) => [b.name.toLowerCase(), b])).values()
      );
      setBrands(uniqueBrands.map((b) => ({ id: b.id, label: b.name })));

      const uniqueThemes = Array.from(
        new Map(themesData.map((t) => [t.name.toLowerCase(), t])).values()
      );
      setThemes(uniqueThemes.map((t) => ({ id: t.id, label: t.name })));

      const uniquePts = Array.from(
        new Map(productTypesData.map((p) => [p.name.toLowerCase(), p])).values()
      );
      setProductTypes(uniquePts.map((p) => ({ id: p.id, label: p.name })));

      const uniqueTcgs = Array.from(
        new Map(tcgsData.map((t) => [t.name.toLowerCase(), t])).values()
      );
      setTcgOptions(
        uniqueTcgs.map((t) => ({
          id: t.id,
          label: t.name,
          slug: t.slug ?? null,
        }))
      );

      setCardLanguages(
        langData.map((l) => ({
          id: l.id,
          label: l.name,
          slug: l.slug ?? null,
          flag: getCardLanguageFlag(l.slug),
        }))
      );

      setCardConditions(cardConditionData.map((c) => ({ id: c.id, label: c.name })));
      setCardFormats(cardFormatData.map((f) => ({ id: f.id, label: f.name })));
      setGradingCompanies(gradingCoData.map((g) => ({ id: g.id, label: g.name })));
    }

    fetchBaseTaxonomy();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedTcgsKey = useMemo(() => selectedTcgs.slice().sort().join(","), [selectedTcgs]);

  useEffect(() => {
    const tcgs = selectedTcgsKey ? selectedTcgsKey.split(",") : [];
    if (tcgs.length === 0) {
      setTcgSets([]);
      setTcgRarities([]);
      return;
    }
    let cancelled = false;
    (async () => {
      let setsData, rarityData;
      try {
        [setsData, rarityData] = await Promise.all([
          getTaxonomyItems({ type: "tcg_set", parent: { mode: "in", ids: tcgs } }),
          getTaxonomyItems({ type: "tcg_rarity", parent: { mode: "in", ids: tcgs } }),
        ]);
      } catch {
        return; // leave sub-taxonomy untouched on load failure (prior behavior)
      }
      if (cancelled) return;
      setTcgSets(setsData.map((s) => ({ id: s.id, label: s.name })));
      setTcgRarities(rarityData.map((r) => ({ id: r.id, label: r.name })));
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTcgsKey]);

  const isMtgSelected = useMemo(() => {
    if (selectedTcgs.length === 0) return false;
    return tcgOptions
      .filter((t) => selectedTcgs.includes(t.id))
      .some((t) => {
        const hay = `${t.slug ?? ""} ${t.label}`.toLowerCase();
        return hay.includes("magic") || hay.includes("mtg");
      });
  }, [selectedTcgs, tcgOptions]);

  const emit = (patch: Partial<FeedFiltersState>) => {
    onChange({ ...value, ...patch });
  };

  const toggleInArray = (arr: string[], id: string): string[] =>
    arr.includes(id) ? arr.filter((v) => v !== id) : [...arr, id];

  const selectListingCategory = (next: ListingCategory) => {
    const toggled: ListingCategory = listingCategory === next ? null : next;
    const patch: Partial<FeedFiltersState> = { listingCategory: toggled };
    if (toggled !== "lego") {
      patch.brands = [];
      patch.themes = [];
      patch.categories = [];
      patch.conditions = [];
      patch.gradeRange = null;
    }
    if (toggled !== "trading_card") {
      patch.tcgs = [];
      patch.tcgSets = [];
      patch.tcgRarities = [];
      patch.cardLanguages = [];
      patch.cardConditions = [];
      patch.cardFormats = [];
      patch.gradingCompanies = [];
      patch.cardGradeRange = null;
      patch.mtgPrintVariants = [];
    }
    emit(patch);
  };

  const toggleBrand = (id: string) => emit({ brands: toggleInArray(selectedBrands, id) });
  const toggleTheme = (id: string) => emit({ themes: toggleInArray(selectedThemes, id) });
  const toggleCategory = (id: string) => emit({ categories: toggleInArray(selectedCategories, id) });
  const toggleCondition = (id: string) => emit({ conditions: toggleInArray(selectedConditions, id) });
  const toggleTcg = (id: string) => emit({ tcgs: toggleInArray(selectedTcgs, id) });
  const toggleTcgSet = (id: string) => emit({ tcgSets: toggleInArray(selectedTcgSets, id) });
  const toggleTcgRarity = (id: string) => emit({ tcgRarities: toggleInArray(selectedTcgRarities, id) });
  const toggleCardLanguage = (id: string) => emit({ cardLanguages: toggleInArray(selectedCardLanguages, id) });
  const toggleCardCondition = (id: string) => emit({ cardConditions: toggleInArray(selectedCardConditions, id) });
  const toggleCardFormat = (id: string) => emit({ cardFormats: toggleInArray(selectedCardFormats, id) });
  const toggleGradingCompany = (id: string) => emit({ gradingCompanies: toggleInArray(selectedGradingCompanies, id) });
  const toggleMtgVariant = (id: string) => emit({ mtgPrintVariants: toggleInArray(selectedMtgVariants, id) });
  const toggleFormat = (id: string) => emit({ listingFormats: toggleInArray(selectedFormats, id) });

  const setGradeRange = (next: [number, number] | null) => emit({ gradeRange: next });
  const setCardGradeRange = (next: [number, number] | null) => emit({ cardGradeRange: next });
  const setSortBy = (next: FeedFiltersState["sortBy"]) => emit({ sortBy: next });

  const clearAllFilters = () => {
    onChange({
      brands: [],
      themes: [],
      categories: [],
      tcgs: [],
      tcgSets: [],
      tcgRarities: [],
      cardLanguages: [],
      cardConditions: [],
      cardFormats: [],
      gradingCompanies: [],
      mtgPrintVariants: [],
      listingFormats: [],
      conditions: [],
      gradeRange: null,
      cardGradeRange: null,
      sortBy: null,
      listingCategory: null,
    });
  };

  const hasActiveFilters =
    listingCategory !== null ||
    selectedBrands.length > 0 ||
    selectedThemes.length > 0 ||
    selectedCategories.length > 0 ||
    selectedTcgs.length > 0 ||
    selectedTcgSets.length > 0 ||
    selectedTcgRarities.length > 0 ||
    selectedCardLanguages.length > 0 ||
    selectedCardConditions.length > 0 ||
    selectedCardFormats.length > 0 ||
    selectedGradingCompanies.length > 0 ||
    cardGradeRange !== null ||
    selectedMtgVariants.length > 0 ||
    selectedFormats.length > 0 ||
    selectedConditions.length > 0 ||
    gradeRange !== null ||
    sortBy !== null;

  const activeCount =
    (listingCategory !== null ? 1 : 0) +
    selectedBrands.length +
    selectedThemes.length +
    selectedCategories.length +
    selectedTcgs.length +
    selectedTcgSets.length +
    selectedTcgRarities.length +
    selectedCardLanguages.length +
    selectedCardConditions.length +
    selectedCardFormats.length +
    selectedGradingCompanies.length +
    (cardGradeRange ? 1 : 0) +
    selectedMtgVariants.length +
    selectedFormats.length +
    selectedConditions.length +
    (gradeRange ? 1 : 0) +
    (sortBy ? 1 : 0);

  const effectiveGradeRange = gradeRange || [CONDITION_GRADE_MIN, CONDITION_GRADE_MAX];
  const effectiveCardGradeRange = cardGradeRange || [1, 10];

  const filterButton = renderButton ? (
    renderButton({
      onClick: () => setShowFiltersAction(!effectiveShowFilters),
      showFilters: effectiveShowFilters,
      hasActiveFilters,
      activeCount,
    })
  ) : null;

  const clearFiltersDraggingSoon = () => {
    window.setTimeout(() => {
      filtersDraggingRef.current = false;
      filtersDragRef.current.active = false;
    }, 0);
  };
  const handleFiltersPointerDown = (event: any) => {
    if (event?.pointerType !== "touch") return;
    filtersDragRef.current.active = true;
    filtersDragRef.current.startX = event.clientX ?? 0;
    filtersDragRef.current.startY = event.clientY ?? 0;
    filtersDraggingRef.current = false;
  };
  const handleFiltersPointerMove = (event: any) => {
    if (event?.pointerType !== "touch") return;
    if (!filtersDragRef.current.active) return;
    if (filtersDraggingRef.current) return;
    const dx = (event.clientX ?? 0) - filtersDragRef.current.startX;
    const dy = (event.clientY ?? 0) - filtersDragRef.current.startY;
    if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      filtersDraggingRef.current = true;
    }
  };
  const handleFiltersPointerUp = () => {
    clearFiltersDraggingSoon();
  };
  const handleFiltersPointerCancel = () => {
    clearFiltersDraggingSoon();
  };
  const blockClickIfDragging = (event: any) => {
    const stripDrag = stripDragRef?.current ?? false;
    if (!filtersDraggingRef.current && !stripDrag) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const chipBtn = (active: boolean, activeClassOverride?: string) =>
    cn(
      "shrink-0 inline-flex items-center gap-1.5 rounded-full h-8 font-medium transition-all duration-200 border",
      chipRow ? "px-2.5 text-xs shadow-sm" : "px-3 text-sm",
      active
        ? (activeClassOverride ?? "bg-primary/10 text-primary border-primary")
        : chipRow
          ? "border-border/40 bg-background/35 text-foreground backdrop-blur-md hover:bg-muted/60 dark:border-white/12 dark:bg-white/5 dark:hover:bg-white/10"
          : "bg-background text-muted-foreground border-border hover:bg-muted"
    );

  const menuSurface: DropdownSurface = chipRow ? "glass" : "panel";
  const clearBtnClass = chipRow
    ? "text-xs font-semibold text-foreground hover:opacity-80 dark:text-white dark:hover:text-white/80"
    : "text-xs font-semibold text-primary hover:text-primary/80";

  const renderCheckboxDropdown = (
    key: string,
    label: string,
    options: FilterOption[],
    selected: string[],
    onToggle: (id: string) => void,
    onClear: () => void,
    clearLabelText: string,
  ) => (
    <DropdownMenu
      open={openDropdown === key}
      onOpenChange={(isOpen) => setOpenDropdown(isOpen ? key : null)}
    >
      <DropdownMenuTrigger asChild>
        <button onClickCapture={blockClickIfDragging} className={chipBtn(selected.length > 0)}>
          <span>{label}</span>
          {selected.length > 0 && (
            <span className="flex items-center justify-center w-4 h-4 text-xs rounded-full bg-primary text-primary-foreground">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        surface={menuSurface}
        className="w-56 max-h-[300px] overflow-y-auto scrollbar-hide"
      >
        {selected.length > 0 && (
          <div className="flex items-center justify-end px-3 py-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className={clearBtnClass}
            >
              {clearLabelText}
            </button>
          </div>
        )}
        {options.length === 0 ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">{tFilters("noOptions")}</div>
        ) : (
          options.map((opt) => (
            <DropdownMenuCheckboxItem
              key={opt.id}
              checked={selected.includes(opt.id)}
              onCheckedChange={() => onToggle(opt.id)}
            >
              <span className="inline-flex items-center gap-2">
                {opt.flag ? <span className="text-base leading-none">{opt.flag}</span> : null}
                <span>{opt.label}</span>
              </span>
            </DropdownMenuCheckboxItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const clearText = tFilters("clear");

  const filterControls = (
    <>
      {!lockCategory && (
        <>
          <button
            onClickCapture={blockClickIfDragging}
            onClick={() => selectListingCategory("lego")}
            className={chipBtn(listingCategory === "lego")}
          >
            <span>{tFilters("buildingBlocks")}</span>
          </button>
          <button
            onClickCapture={blockClickIfDragging}
            onClick={() => selectListingCategory("trading_card")}
            className={chipBtn(listingCategory === "trading_card")}
          >
            <span>{tFilters("tradingCardGames")}</span>
          </button>
          <button
            onClickCapture={blockClickIfDragging}
            onClick={() => selectListingCategory("comic")}
            className={chipBtn(listingCategory === "comic")}
          >
            <span>{tFilters("comics")}</span>
          </button>
        </>
      )}

      {listingCategory === "lego" &&
        renderCheckboxDropdown(
          "brands",
          tFilters("brand"),
          brands,
          selectedBrands,
          toggleBrand,
          () => emit({ brands: [] }),
          clearText
        )}

      {listingCategory === "lego" &&
        renderCheckboxDropdown(
          "themes",
          tFilters("theme"),
          themes,
          selectedThemes,
          toggleTheme,
          () => emit({ themes: [] }),
          clearText
        )}

      {listingCategory === "lego" &&
        renderCheckboxDropdown(
          "productTypes",
          tFilters("productType"),
          productTypes,
          selectedCategories,
          toggleCategory,
          () => emit({ categories: [] }),
          clearText,
        )}

      {listingCategory === "trading_card" &&
        renderCheckboxDropdown(
          "tcgs",
          tFilters("tcg"),
          tcgOptions,
          selectedTcgs,
          toggleTcg,
          () => emit({ tcgs: [] }),
          clearText
        )}

      {listingCategory === "trading_card" && selectedTcgs.length > 0 && tcgSets.length > 0 &&
        renderCheckboxDropdown(
          "tcgSets",
          tFilters("tcgSet"),
          tcgSets,
          selectedTcgSets,
          toggleTcgSet,
          () => emit({ tcgSets: [] }),
          clearText
        )}

      {listingCategory === "trading_card" && selectedTcgs.length > 0 && tcgRarities.length > 0 &&
        renderCheckboxDropdown(
          "tcgRarities",
          tFilters("tcgRarity"),
          tcgRarities,
          selectedTcgRarities,
          toggleTcgRarity,
          () => emit({ tcgRarities: [] }),
          clearText
        )}

      {listingCategory === "trading_card" && cardLanguages.length > 0 &&
        renderCheckboxDropdown(
          "cardLanguages",
          tFilters("cardLanguage"),
          cardLanguages,
          selectedCardLanguages,
          toggleCardLanguage,
          () => emit({ cardLanguages: [] }),
          clearText
        )}

      {listingCategory === "trading_card" && cardFormats.length > 0 &&
        renderCheckboxDropdown(
          "cardFormats",
          tFilters("cardFormat"),
          cardFormats,
          selectedCardFormats,
          toggleCardFormat,
          () => emit({ cardFormats: [] }),
          clearText
        )}

      {listingCategory === "trading_card" && cardConditions.length > 0 &&
        renderCheckboxDropdown(
          "cardConditions",
          tFilters("cardCondition"),
          cardConditions,
          selectedCardConditions,
          toggleCardCondition,
          () => emit({ cardConditions: [] }),
          clearText
        )}

      {listingCategory === "trading_card" && gradingCompanies.length > 0 &&
        renderCheckboxDropdown(
          "gradingCompanies",
          tFilters("gradingCompany"),
          gradingCompanies,
          selectedGradingCompanies,
          toggleGradingCompany,
          () => emit({ gradingCompanies: [] }),
          clearText
        )}

      {listingCategory === "trading_card" && isMtgSelected &&
        renderCheckboxDropdown(
          "mtgVariants",
          tFilters("mtgVariant"),
          MTG_VARIANTS.map((v) => ({ id: v.value, label: v.label })),
          selectedMtgVariants,
          toggleMtgVariant,
          () => emit({ mtgPrintVariants: [] }),
          clearText
        )}

      <DropdownMenu open={openDropdown === "formats"} onOpenChange={(o) => setOpenDropdown(o ? "formats" : null)}>
        <DropdownMenuTrigger asChild>
          <button
            onClickCapture={blockClickIfDragging}
            className={chipBtn(
              selectedFormats.length > 0,
              selectedFormats.length > 0 ? listingFormatFilterTriggerActiveClasses(selectedFormats) : undefined
            )}
          >
            <span>{tFilters("format")}</span>
            {selectedFormats.length > 0 && (
              <span className="flex items-center justify-center w-4 h-4 text-xs rounded-full bg-primary text-primary-foreground">
                {selectedFormats.length}
              </span>
            )}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" surface={menuSurface} className="w-56 scrollbar-hide">
          {selectedFormats.length > 0 && (
            <div className="flex items-center justify-end px-3 py-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  emit({ listingFormats: [] });
                }}
                className={clearBtnClass}
              >
                {clearText}
              </button>
            </div>
          )}
          {LISTING_FORMAT_OPTIONS.map((fmt) => (
            <DropdownMenuCheckboxItem
              key={fmt.value}
              checked={selectedFormats.includes(fmt.value)}
              onCheckedChange={() => toggleFormat(fmt.value)}
            >
              {tFormat(fmt.label as any)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {listingCategory === "lego" && (
        <DropdownMenu open={openDropdown === "conditions"} onOpenChange={(o) => setOpenDropdown(o ? "conditions" : null)}>
          <DropdownMenuTrigger asChild>
            <button onClickCapture={blockClickIfDragging} className={chipBtn(selectedConditions.length > 0)}>
              <span>{tFilters("condition")}</span>
              {selectedConditions.length > 0 && (
                <span className="flex items-center justify-center w-4 h-4 text-xs rounded-full bg-primary text-primary-foreground">
                  {selectedConditions.length}
                </span>
              )}
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" surface={menuSurface} className="w-56 scrollbar-hide">
            {selectedConditions.length > 0 && (
              <div className="flex items-center justify-end px-3 py-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    emit({ conditions: [] });
                  }}
                  className={clearBtnClass}
                >
                  {clearText}
                </button>
              </div>
            )}
            {CONDITIONS.map((condition) => (
              <DropdownMenuCheckboxItem
                key={condition.value}
                checked={selectedConditions.includes(condition.value)}
                onCheckedChange={() => toggleCondition(condition.value)}
              >
                {tFilters(condition.label as any)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {listingCategory === "lego" && (
        <DropdownMenu open={openDropdown === "grading"} onOpenChange={(o) => setOpenDropdown(o ? "grading" : null)}>
          <DropdownMenuTrigger asChild>
            <button onClickCapture={blockClickIfDragging} className={chipBtn(Boolean(gradeRange))}>
              <span>{tFilters("grading")}</span>
              {gradeRange && (
                <span className="text-xs">
                  {gradeRange[0]}-{gradeRange[1]}
                </span>
              )}
              <ChevronDown className={cn("h-3 w-3 transition-transform", openDropdown === "grading" && "rotate-180")} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" surface={menuSurface} className="w-[24rem] p-4">
            <GradeRangeFilterPanel
              value={effectiveGradeRange}
              min={CONDITION_GRADE_MIN}
              max={CONDITION_GRADE_MAX}
              step={1}
              onValueChange={(next) => setGradeRange([next[0], next[1]])}
              onClear={() => setGradeRange(null)}
              clearLabel={clearText}
              clearButtonClassName={clearBtnClass}
              hint={tFilters("gradeRangeHint")}
              labels={CONDITION_GRADE_LABELS}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {listingCategory === "trading_card" && (
        <DropdownMenu open={openDropdown === "cardGrading"} onOpenChange={(o) => setOpenDropdown(o ? "cardGrading" : null)}>
          <DropdownMenuTrigger asChild>
            <button onClickCapture={blockClickIfDragging} className={chipBtn(Boolean(cardGradeRange))}>
              <span>{tFilters("cardGrade")}</span>
              {cardGradeRange && (
                <span className="text-xs">
                  {cardGradeRange[0]}-{cardGradeRange[1]}
                </span>
              )}
              <ChevronDown className={cn("h-3 w-3 transition-transform", openDropdown === "cardGrading" && "rotate-180")} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" surface={menuSurface} className="w-[24rem] p-4">
            <GradeRangeFilterPanel
              value={effectiveCardGradeRange}
              min={1}
              max={10}
              step={0.5}
              onValueChange={(next) => setCardGradeRange([next[0], next[1]])}
              onClear={() => setCardGradeRange(null)}
              clearLabel={clearText}
              clearButtonClassName={clearBtnClass}
              hint={tFilters("gradeRangeHint")}
              valueSuffix=""
              showTickLabels={false}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <button
        onClickCapture={blockClickIfDragging}
        onClick={() => {
          if (sortBy === "price-asc") {
            setSortBy("price-desc");
          } else if (sortBy === "price-desc") {
            setSortBy(null);
          } else {
            setSortBy("price-asc");
          }
        }}
        className={chipBtn(Boolean(sortBy?.startsWith("price")))}
      >
        <span>{tFilters("price")}</span>
        {sortBy === "price-asc" && <span className="text-xs">↑</span>}
        {sortBy === "price-desc" && <span className="text-xs">↓</span>}
      </button>

      {(activeTab === "live" || activeTab === "discover") && (
        <button
          onClickCapture={blockClickIfDragging}
          onClick={() => {
            if (sortBy === "date-asc") {
              setSortBy("date-desc");
            } else if (sortBy === "date-desc") {
              setSortBy(null);
            } else {
              setSortBy("date-asc");
            }
          }}
          className={chipBtn(Boolean(sortBy?.startsWith("date")))}
        >
          <span>{tFilters("listed")}</span>
          {sortBy === "date-asc" && <span className="text-xs">↑</span>}
          {sortBy === "date-desc" && <span className="text-xs">↓</span>}
        </button>
      )}

      {hasActiveFilters && (
        <button
          onClickCapture={blockClickIfDragging}
          onClick={clearAllFilters}
          className={cn(
            "shrink-0 inline-flex items-center gap-1.5 rounded-full h-8 font-medium transition-all duration-200 border bg-destructive/10 text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground",
            chipRow ? "px-2.5 text-xs shadow-sm" : "px-3 text-sm"
          )}
        >
          <X className="h-3 w-3" />
          <span>{tFilters("clear")}</span>
        </button>
      )}
    </>
  );

  const content = (
    <div className="relative pointer-events-auto">
      {effectiveShowFilters && (
        <div className="px-4 pb-2">
          <div
            className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 md:justify-center"
            onPointerDown={handleFiltersPointerDown}
            onPointerMove={handleFiltersPointerMove}
            onPointerUp={handleFiltersPointerUp}
            onPointerCancel={handleFiltersPointerCancel}
          >
            {filterControls}
          </div>
        </div>
      )}
    </div>
  );

  if (inline && chipRow) {
    return (
      <>
        {filterButton}
        {effectiveShowFilters ? filterControls : null}
      </>
    );
  }

  if (inline) {
    return (
      <>
        {filterButton}
        {content}
      </>
    );
  }

  if (!mounted) return filterButton;
  if (!host) return filterButton;
  return (
    <>
      {filterButton}
      {createPortal(content, host)}
    </>
  );
}

export function FilterButton({
  onClick,
  showFilters,
  hasActiveFilters,
  activeCount,
}: {
  onClick: () => void;
  showFilters: boolean;
  hasActiveFilters: boolean;
  activeCount: number;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "shrink-0 relative inline-flex items-center justify-center rounded-full w-10 h-10 transition-all duration-200 backdrop-blur-md border border-border/20",
        showFilters || hasActiveFilters
          ? "bg-background/50 text-foreground"
          : "bg-background/30 text-foreground"
      )}
    >
      <Filter className="h-5 w-5" />
    </button>
  );
}
